
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";

interface ImageErrorBoundaryProps {
  children: ReactNode;
  fallbackUrl?: string;
  onRetry?: () => void;
  className?: string;
}

interface ImageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ImageErrorBoundary extends Component<ImageErrorBoundaryProps, ImageErrorBoundaryState> {
  constructor(props: ImageErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ImageErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    console.log("ImageErrorBoundary caught error:", error.message);
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error("Image rendering error:", error);
    console.error("Error component stack:", errorInfo.componentStack);
  }

  handleRetry = (): void => {
    // Reset the error state
    this.setState({
      hasError: false,
      error: null
    });
    
    // Call the onRetry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className={`flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-md ${this.props.className || ""}`}>
          <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
          <p className="text-sm text-slate-600 text-center mb-3">
            {this.state.error?.message || "Failed to load image"}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={this.handleRetry} 
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-3 w-3" />
            Try again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
